import {
 Box,
 Checkbox,
 CircularProgress,
 FormControlLabel,
 FormGroup,
 Typography,
} from '@mui/material';
import { AccessGroupsDTO, GroupDTO } from '@redux/identity/identityTypes';
import { useTranslation } from 'react-i18next';

interface UserAccessGroupsSectionProps {
 groups?: GroupDTO[] | null | string;
 accessGroups?: AccessGroupsDTO[];
 selectedGroupIds: number[];
 selectedAccessGroupIds: string[];
 onGroupToggle: (groupId: number, isChecked: boolean) => void;
 onAccessGroupToggle: (accessGroupId: string, isChecked: boolean) => void;
 loadingGroups?: boolean;
 loadingAccessGroups?: boolean;
 disabled?: boolean;
 isModal?: boolean;
}

export const UserAccessGroupsSection = ({
 groups,
 accessGroups,
 selectedGroupIds,
 selectedAccessGroupIds,
 onGroupToggle,
 onAccessGroupToggle,
 loadingGroups = false,
 loadingAccessGroups = false,
 disabled = false,
 isModal = false,
}: UserAccessGroupsSectionProps) => {
 const { t } = useTranslation();
 return (
  <Box sx={{ p: 0 }}>
   <Typography
    sx={{
     fontSize: 14,
     fontWeight: 500,
     pt: isModal ? 0 : 3,
     color: (theme) =>
      isModal ? theme.palette.kemek.darkGray : theme.palette.kemek.darkestGray,
     pb: 1,
    }}
   >
    {t('user.sidebar.groups.userGroups')}
   </Typography>
   {loadingGroups && (
    <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
     <CircularProgress size={24} />
    </Box>
   )}
   {!loadingGroups && Array.isArray(groups) && (
    <FormGroup
     sx={{
      py: 1,
      px: 1,
      border: '1px solid',
      borderColor: (theme) => theme.palette.kemek.lightestGray,
      borderRadius: '4px',
     }}
    >
     {groups?.map((group) => (
      <FormControlLabel
       key={group.Id}
       control={
        <Checkbox
         checked={selectedGroupIds.includes(group.Id)}
         onChange={(e) => onGroupToggle(group.Id, e.target.checked)}
         disabled={disabled}
        />
       }
       label={
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
         <Typography
          sx={{
           fontSize: 12,
           color: selectedGroupIds.includes(group.Id)
            ? (theme) => theme.palette.kemek.blue
            : (theme) => theme.palette.kemek.darkGray,
          }}
         >
          {group.Name ||
           `${t('user.sidebar.groups.groupFallback')} ${group.Id}`}
         </Typography>
         {group.Description && (
          <Typography
           sx={{ fontSize: 12, color: (theme) => theme.palette.kemek.darkGray }}
          >
           {group.Description}
          </Typography>
         )}
        </Box>
       }
      />
     ))}
    </FormGroup>
   )}
   <Typography
    sx={{
     fontSize: 14,
     fontWeight: 500,
     pt: isModal ? 2 : 3,
     color: (theme) =>
      isModal ? theme.palette.kemek.darkGray : theme.palette.kemek.darkestGray,
     pb: 1,
    }}
   >
    {t('user.sidebar.groups.accessGroups')}
   </Typography>
   {loadingAccessGroups ||
    (!Array.isArray(accessGroups) && (
     <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
      <CircularProgress size={24} />
     </Box>
    ))}
   {!loadingAccessGroups && (
    <FormGroup
     sx={{
      py: 1,
      px: 1,
      border: '1px solid',
      borderColor: (theme) => theme.palette.kemek.lightestGray,
      borderRadius: '4px',
      display: 'flex',
      flexDirection: 'column',
      gap: 1,
     }}
    >
     {accessGroups?.map((accessGroup) => {
      const accessGroupId = accessGroup.Id || '';
      const isSelected = selectedAccessGroupIds.includes(accessGroupId);
      return (
       <FormControlLabel
        key={accessGroupId}
        sx={{ alignItems: 'flex-start' }}
        control={
         <Checkbox
          checked={isSelected}
          onChange={(e) => onAccessGroupToggle(accessGroupId, e.target.checked)}
          disabled={disabled}
          sx={{ pt: '4px' }}
         />
        }
        label={
         <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Typography
           sx={{
            fontSize: 12,
            fontWeight: 500,
            mt: '3px',
            color: isSelected
             ? (theme) => theme.palette.kemek.blue
             : (theme) => theme.palette.kemek.darkGray,
           }}
          >
           {accessGroup.Name ||
            `${t('user.sidebar.groups.accessGroupFallback')} ${accessGroup.Id}`}
          </Typography>
          {accessGroup.Description && (
           <Typography
            sx={{
             fontSize: 12,
             color: (theme) => theme.palette.kemek.darkGray,
            }}
           >
            {accessGroup.Description}
           </Typography>
          )}
         </Box>
        }
       />
      );
     })}
    </FormGroup>
   )}
  </Box>
 );
};
